import React, { useEffect, useState } from 'react';
import '../solutions/Solutions.css'; // you can style it here
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase'; // adjust based on your structure
import { Helmet } from 'react-helmet-async';


// AI
// Data Center
// Central Office & Headend
// Outside plant
// FTTx & MDU
// Rural Broadband
// Wireless
// Enterprise
// Medical Imaging
// Neuroscience
// Optoelectronics
// Specialty Optics
// Optical Equipment

const slides = [
  {
    video: "/videos/solutionCentralOffice.mp4",
    poster: "/videos/solutionCentralOffice.jpg",
    heading: "Ask the expert: Your cable assembly questions answered by Go!Foton",
    subheading: "Dec 2, 2025",
    buttonLink: "/support"
  }
];

function BlogCableAssemblyQuestionsAnswered() {

const [products, setProducts] = useState([]);
const [filteredProducts, setFilteredProducts] = useState([]);
const [activeFilter, setActiveFilter] = useState('All');

const [open, setOpen] = useState(new Set());

  const toggle = (key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };


const productFamilies = [
  'Fiber Cable Assemblies',
  'Fiber Panels',
  'Frames',
];

  const [industryFilter, setIndustryFilter] = useState('Central Office & Headends')

const topByFamily = productFamilies.map(famName => {
  const matchingProducts = filteredProducts.filter(
    p => (p.productFamily || '').trim() === famName
  );
  if (matchingProducts.length === 0) return null;

  const topProduct = matchingProducts.reduce((best, current) =>
    Number(current.rank ?? Infinity) < Number(best.rank ?? Infinity) ? current : best
  );

  return { family: famName, topProduct };
}).filter(Boolean); // remove nulls if a family has no products



useEffect(() => {
  const loadProducts = async () => {
    const querySnapshot = await getDocs(collection(db, 'products'));
    const allProducts = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => (p["Include Y or N"] || '').trim().toUpperCase() !== 'N');

    setProducts(allProducts);
    setFilteredProducts(allProducts); // Default to all
  };

  loadProducts();
}, []);

useEffect(() => {
  if (activeFilter === 'All') {
    setFilteredProducts(products);
  } else {
    setFilteredProducts(products.filter(p => p.productFamily === activeFilter));
  }
}, [activeFilter, products]);

  return (
    <div className="contact-page">

<Helmet>
      <title>Ask the expert: Your cable assembly questions answered by Go!Foton</title>

      <meta
        name="description"
        content="In this blog from Go!Foton, we talk about the fundamentals of fiber cables: their components, applications, maintenance, lifespan, attributes, and differentiation."
      />

      <meta
        property="og:title"
        content="Ask the expert: Your cable assembly questions answered by Go!Foton"
      />
      <meta
        property="og:description"
        content="In this blog from Go!Foton, we talk about the fundamentals of fiber cables: their components, applications, maintenance, lifespan, attributes, and differentiation."
      />
      <meta
        property="og:image"
        content="https://gofoton.com/images/blog/cablesBlog.jpeg"
      />
      <meta
        property="og:url"
        content="https://gofoton.com/cable-assembly-questions-answered"
      />
      <meta property="og:type" content="article" />
    </Helmet>


      <div className="curved-header-sol2" style={{paddingBottom: 60}}>
        <div className="datacenter-left" style={{ paddingTop: 100}}>
            <h1>{slides[0].heading}</h1>
            <p style={{ marginBottom: 40, fontSize: 20}}>{slides[0].subheading}</p>
            </div>
          <div className="datacenter-right"  style={{ paddingTop: 60}}>
              <img src="/images/blog/cablesBlog.jpeg" className="datacenter-img" />
            </div>
            </div>


      <div className="blog-section-2" style={{ marginTop: 30}}>

 <p className="solution-section-1-description">
    Technology in the network physical layer is changing at a pace greater than ever before. 
    New applications like AI are driving higher and higher data rates, creating a cascade of upgrades 
    and ripple effects throughout the technology stack. The bottom line is that more compute power means 
    more fiber connectivity and more fiber cables.
    <br/><br/>

    Here, we’re talking about the fundamentals of fiber cables. 
    These pervasive products are a critical part of the infrastructure carrying the load for most of the 
    communications experiences we depend on. Whether you’re new to the industry or seeking a quick primer, 
    these questions and answers about cable assemblies will guide you through the evolution of the 
    infrastructure as we depend on fiber cables more.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50}}>1. How many types of optical cable connectors are there?</h4>
  <p className="solution-section-1-description" style={{ marginTop: 0, paddingTop: 0}}>
    There are multiple connector types in use today, highly dependent on application and market. 
    Telecom/ISP markets typically use SC, LC, and MPO connectors. The data center environment has 
    historically relied on LC and MPO but is now transitioning into Very Small Form Factor (VSFF) connectors, 
    which offer a smaller footprint and significantly higher fiber density.
  </p>

  <h4 style={{ textAlign: 'left' , marginTop: 50}}>2. What are the core elements of most cable assemblies?</h4>
  <p className="solution-section-1-description">
    In the simplest case, a fiber cable assembly can consist of a single fiber cable with two connectors — 
    one on each end. In today’s multi-device world, high-speed data transfer demands more fibers, making 
    assemblies with up to 288 fibers and multi-fiber connectors extremely common.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50 }}>3. Are different cables used for different kinds of applications?</h4>
  <p className="solution-section-1-description">
    Absolutely. The first question is whether the cable supports an indoor or outdoor application — 
    this determines the starting point. Cable construction, jacket flame rating, and fiber type must all 
    be considered when selecting the correct fiber optic cable.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50 }}>4. What is the typical lifespan of a fiber cable assembly?</h4>
  <p className="solution-section-1-description">
    The long-standing industry standard for optical fiber lifespan is about 20 years. However, outdoor 
    installations like Optical Ground Wire (OPGW) frequently continue functioning for 30–40 years.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50 }}>5. How do you maintain cable infrastructure to ensure maximum longevity?</h4>
  <p className="solution-section-1-description">
    This is heavily dependent on application. Clean, climate-controlled environments yield the longest 
    lifespan for optical networks. Outdoor systems face additional challenges including weather events, 
    rodents, and even electromagnetic factors in some aerial installations.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50 }}>6. There are numerous cable manufacturers in the market — how do you differentiate them?</h4>
  <p className="solution-section-1-description">
    Quality and performance remain top differentiators. Beyond those, aesthetics have become increasingly 
    important — especially in data centers, where operators consider the layout a “showpiece.” 
    Color consistency across different production batches can strongly influence purchasing decisions. 
    The industry uses the Munsell color wheel, which allows wide variation, making consistency a priority.
  </p>

  <h4 style={{ textAlign: 'left' , marginTop: 50}}>7. What are the most important performance specifications to consider when selecting a cable assembly?</h4>
  <p className="solution-section-1-description">
    Focus on <b>insertion loss</b> and <b>return loss</b>. These optical performance metrics directly affect 
    the speed and quality of data transmission. Even a small difference of a few dB over long distances can 
    degrade signal quality and slow a network.
  </p>

  <h4 style={{ textAlign: 'left', marginTop: 50 }}>
    8. What are the cable assembly industry standards and certifications to look for?
  </h4>
  <p className="solution-section-1-description">
    Depending on the connector type, <b>GR-326</b> and <b>GR-1435</b> are the de facto standards. GR-326 covers basic jumpers (LC, SC, etc.), while GR-1435 applies to multi-fiber connectors such as MPO (12f, 16f, 24f, etc.). These standards ensure optical, mechanical, and environmental performance.
  </p>

  {/* closing section – not a question, just body text */}
  <p className="solution-section-1-description" style={{ marginTop: 50 }}>
    For more information about cable assemblies, view our <a href="/fiber-cable-assemblies" target="_blank">portfolio pages</a> and explore more blogs from Go!Foton:
    <br/><br/>

    <a href="/what-are-VSFF-connectors-and-why-should-you-use-them" target="_blank" className="cta-button">
      What are VSFF connectors and why should you use them
    </a>
    <br/>
    <br />
    <a href="/fiber-optic-basics-in-the-age-of-ai-datacenters" target="blank" className="cta-button" style={{ marginTop: 50}}>
      Fiber optic basics in the age of AI data centers
    </a>

    <br/><br/>

  </p>

      </div>


        <div className="curved-footer-solutions">
        <div className="curved-top-overlay-solutions" style={{ backgroundColor: 'white'}}></div>
        
        <h2>Would you like to hear more?</h2>
        <p>Our experts would love to hear about your systems and how we can help</p>
        <a className="connect-button" href="/support">LET’S CONNECT</a>
        </div>
    </div>
  );
}

export default BlogCableAssemblyQuestionsAnswered;
